import { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconMinus, IconPlus, IconDeviceDesktop } from "@tabler/icons-react";
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import { PAYPAL_CLIENT_ID } from "../../../config/paypal";
import { getLineTotal, FULL_PRICE_PCS } from "../PayPalCheckout/UseCart";
import { Edition, Duration, daysForDuration } from "../../Settings";
import { DEBUG } from "../..";
import classes from "./Manager.module.css";

export interface Binding {
  steam_id: string;
  releasable_at: string | null; // ISO, or null = releasable now
  is_self: boolean;
}
export interface KeyInfo {
  key_ident: string;
  edition: Edition;
  type: "lifetime" | "subscription";
  quantity: number;
  valid_until: string | null;
  bindings: Binding[];
}

export interface License {
  key_ident: string;
  edition: Edition;
  type: "lifetime" | "subscription";
  currentQty: number; // current seat count, from the caller (works even if get-keys is unavailable)
  // When false, only device release is offered (no seat-count stepper) — used for keys
  // whose count can't be changed here (subscriptions in the licenses list, non-LMUTV keys).
  canChangeCount?: boolean;
  subscription_id?: string;
  duration?: Duration; // subscription only (for the proration period)
  next_billing_date?: string | null; // subscription only
}

const MAX_PCS = 50;
const editionLabel = (e: Edition) => (e === "pro" ? "LMU TV Pro" : "LMU TV Standard");

async function loadKey(keyIdent: string): Promise<KeyInfo | null> {
  try {
    const r = await fetch("/dyn/get-keys.php", { credentials: "include" });
    const all: KeyInfo[] = await r.json();
    return (Array.isArray(all) ? all : []).find((k) => k.key_ident === keyIdent) ?? null;
  } catch (e) {
    if (DEBUG) console.error("loadKey", e);
    return null;
  }
}

// Price for `qty` seats of this tier (bulk-tiered). Lifetime uses the lifetime tier.
function tierTotal(edition: Edition, type: "lifetime" | "subscription", duration: Duration | undefined, qty: number): number {
  const d: Duration = type === "lifetime" ? "lifetime" : (duration ?? "1y");
  return getLineTotal({ edition, duration: d, pcs: qty });
}

// The one-time charge to GROW from oldQty→newQty. Lifetime = full delta; subscription =
// delta prorated over the days left in the current period.
function increaseAmount(license: License, oldQty: number, newQty: number): number {
  const delta = tierTotal(license.edition, license.type, license.duration, newQty)
    - tierTotal(license.edition, license.type, license.duration, oldQty);
  if (license.type === "lifetime") return Math.max(0, delta);

  const periodDays = license.duration ? daysForDuration(license.duration) ?? 0 : 0;
  let factor = 1;
  if (periodDays > 0 && license.next_billing_date) {
    const msLeft = new Date(license.next_billing_date + "T00:00:00").getTime() - Date.now();
    const daysLeft = Math.max(1, Math.min(periodDays, Math.ceil(msLeft / 86400000)));
    factor = daysLeft / periodDays;
  }
  return Math.max(0, delta * factor);
}

// ── PayPal one-time capture for a seat increase ──────────────────────────────
function PayGrow({ amount, onCaptured, onError }: {
  amount: number; onCaptured: (orderId: string) => void; onError: () => void;
}) {
  const [{ isPending }] = usePayPalScriptReducer();
  if (isPending) return <Center py="sm"><Loader color="lmuTVColors" /></Center>;
  return (
    // PayPal reads the mount container's solid background-color and paints the button
    // iframe body with it. A CSS var / transparent isn't picked up (defaults to white), so
    // set the modal's dark surface explicitly to kill the white gaps between the buttons.
    <div style={{ backgroundColor: "#242424" }} className={classes.managerContainer}>
    {/* All funding sources (incl. the standalone SEPA button) — restricting to the PayPal
        button alone would drop direct SEPA debit, which matters for the DE market. */}
    <PayPalButtons
      // Re-render the order when the amount changes (stepping seats) WITHOUT reloading the
      // SDK — avoids the buttons flickering/reloading on every +/-.
      forceReRender={[amount]}
      style={{ layout: "vertical", color: "black", shape: "rect" }}
      createOrder={(_d, actions) =>
        actions.order.create({
          intent: "CAPTURE",
          purchase_units: [{ amount: { value: amount.toFixed(2), currency_code: "EUR" } }],
        })
      }
      onApprove={async (_d, actions) => {
        try {
          const details = await actions.order!.capture();
          onCaptured(details.id!);
        } catch (e) { if (DEBUG) console.error(e); onError(); }
      }}
      onError={() => onError()}
    />
    </div>
  );
}

type Phase = "idle" | "busy" | "error" | "success";

export function SeatManager({ license, opened, onClose, onChanged }: {
  license: License | null;
  opened: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation("translation");
  const [info, setInfo] = useState<KeyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [newQty, setNewQty] = useState(1);
  const [phase, setPhase] = useState<Phase>("idle");

  const refresh = async () => {
    if (!license) return;
    setLoading(true);
    // get-keys gives us the live seat count + device bindings; if it's unavailable we
    // still manage seats using the count the caller passed in (bindings just show empty).
    const k = await loadKey(license.key_ident);
    const resolved: KeyInfo = k ?? {
      key_ident: license.key_ident,
      edition: license.edition,
      type: license.type,
      quantity: license.currentQty,
      valid_until: license.next_billing_date ?? null,
      bindings: [],
    };
    setInfo(resolved);
    setNewQty(resolved.quantity);
    setLoading(false);
  };

  useEffect(() => {
    if (opened && license) { setPhase("idle"); refresh(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, license?.key_ident]);

  if (!license) return null;

  const canCount = license.canChangeCount !== false; // default true (subscription flow)
  const oldQty = info?.quantity ?? 1;
  const decreasing = newQty < oldQty;
  const increasing = newQty > oldQty;
  const grow = increasing ? increaseAmount(license, oldQty, newQty) : 0;
  const needsPayment = increasing && grow > 0.0;

  const endpoint = license.type === "lifetime" ? "/dyn/change-lifetime-pcs.php" : "/dyn/change-subscription-pcs.php";

  const applyChange = async (orderId?: string) => {
    setPhase("busy");
    try {
      const fd = new FormData();
      fd.append("quantity", String(newQty));
      if (license.type === "lifetime") fd.append("key_ident", license.key_ident);
      else fd.append("subscription_id", license.subscription_id ?? "");
      if (orderId) fd.append("order_id", orderId);
      const r = await fetch(endpoint, { method: "POST", body: fd, credentials: "include" });
      const d = await r.json();
      if (!d.success) { setPhase("error"); return; }
      // A recurring increase/decrease may need PayPal buyer approval → redirect there.
      if (d.approve) { window.location.href = d.approve; return; }
      setPhase("success");
      onChanged();
    } catch (e) { if (DEBUG) console.error(e); setPhase("error"); }
  };

  const releaseSeat = async (steamId: string) => {
    setPhase("busy");
    try {
      const fd = new FormData();
      fd.append("key_ident", license.key_ident);
      fd.append("steam_id", steamId);
      const r = await fetch("/dyn/release-seat.php", { method: "POST", body: fd, credentials: "include" });
      const d = await r.json();
      if (!d.success) { setPhase("error"); return; }
      await refresh();
      setPhase("idle");
    } catch (e) { if (DEBUG) console.error(e); setPhase("error"); }
  };

  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

  return (
    <Modal
      opened={opened}
      onClose={() => { if (phase !== "busy") onClose(); }}
      title={<Title order={4}>{t("account.seats.title")}</Title>}
      size="md"
      centered
    >
      {loading ? (
        <Center py="xl"><Loader color="lmuTVColors" /></Center>
      ) : !info ? (
        <Alert color="yellow" radius="md">{t("account.seats.notFound")}</Alert>
      ) : phase === "success" ? (
        <Alert color="green" radius="md" title={t("account.seats.successTitle")}>
          <Text size="sm" mb="sm">{t("account.seats.successMsg")}</Text>
          <Button color="lmuTVColors" fullWidth onClick={onClose}>{t("menu.user.account")}</Button>
        </Alert>
      ) : (
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={600}>{editionLabel(info.edition)}</Text>
            <Badge variant="light" color={info.type === "lifetime" ? "lmuTVColors" : "gray"}>
              {info.type === "lifetime" ? t("cart.durations.lifetime") : t("account.seats.subscription")}
            </Badge>
          </Group>

          {/* Seat-count management. The PayPal provider stays mounted the whole time (SDK
              loads once) and the buttons re-render on amount change via forceReRender, so
              stepping seats no longer reloads/flickers. A min-height action zone keeps the
              modal from jumping as the note/buttons swap in and out. */}
          {canCount && (
            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR", intent: "capture", disableFunding: "credit" }}>
              <Group justify="center" gap={44} wrap="nowrap">
                <ActionIcon size={42} radius="xl" variant="filled" color="lmuTVColors"
                  disabled={newQty <= oldQty || phase === "busy"} onClick={() => setNewQty((q) => Math.max(oldQty, q - 1))}>
                  <IconMinus size={20} />
                </ActionIcon>
                <Stack gap={0} align="center" miw={120}>
                  <Text size="xl" fw={800}>{newQty}</Text>
                  <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                    {t("cart.pcs")} ({t("account.seats.now")}: {oldQty})
                  </Text>
                </Stack>
                <ActionIcon size={42} radius="xl" variant="filled" color="lmuTVColors"
                  disabled={newQty >= MAX_PCS || phase === "busy"} onClick={() => setNewQty((q) => Math.min(MAX_PCS, q + 1))}>
                  <IconPlus size={20} />
                </ActionIcon>
              </Group>

              <div style={{ minHeight: 96, marginTop: 12 }}>
                {newQty > FULL_PRICE_PCS && <Text ta="center" size="xs" c="lmuTVColors.4" mb={6}>{t("cart.bulkNote")}</Text>}
                {increasing && (
                  <Alert color="lmuTVColors" radius="md" variant="light" mb="sm">
                    <Text size="sm">
                      {license.type === "lifetime" ? t("account.seats.lifetimeIncreaseNote") : t("account.seats.subIncreaseNote")}
                    </Text>
                    {needsPayment && <Text fw={700} mt={4}>{grow.toFixed(2)}€</Text>}
                  </Alert>
                )}
                {decreasing && (
                  <Alert color="yellow" radius="md" variant="light" mb="sm">
                    <Text size="sm">
                      {license.type === "lifetime" ? t("account.seats.lifetimeDecreaseNote") : t("account.seats.subDecreaseNote")}
                      {license.type !== "lifetime" && license.next_billing_date ? ` (${fmtDate(license.next_billing_date)})` : ""}
                    </Text>
                  </Alert>
                )}
                {newQty !== oldQty && (
                  needsPayment ? (
                    <PayGrow amount={Number(grow.toFixed(2))} onCaptured={(oid) => applyChange(oid)} onError={() => setPhase("error")} />
                  ) : (
                    <Button color="lmuTVColors" fullWidth loading={phase === "busy"} onClick={() => applyChange()}>
                      {t("account.seats.apply")}
                    </Button>
                  )
                )}
              </div>
            </PayPalScriptProvider>
          )}

          {phase === "error" && (
            <Alert color="red" radius="md" withCloseButton onClose={() => setPhase("idle")}>
              {t("account.seats.errorMsg")}
            </Alert>
          )}

          <Divider label={t("account.seats.devices")} labelPosition="center" />

          {/* Device / seat list with self-service release */}
          {info.bindings.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center">{t("account.seats.noDevices")}</Text>
          ) : (
            <Stack gap="xs">
              {info.bindings.map((b) => {
                const locked = !!b.releasable_at;
                return (
                  <Group key={b.steam_id} justify="space-between" wrap="nowrap">
                    <Group gap={8} wrap="nowrap">
                      <IconDeviceDesktop size={16} />
                      <Text size="sm" style={{ fontFamily: "monospace" }}>{b.steam_id}</Text>
                      {b.is_self && <Badge size="xs" variant="light">{t("account.seats.thisDevice")}</Badge>}
                    </Group>
                    {locked ? (
                      <Text size="xs" c="dimmed">{t("account.seats.freeFrom")}: {fmtDate(b.releasable_at!)}</Text>
                    ) : (
                      <Button size="xs" variant="subtle" color="red" disabled={phase === "busy"} onClick={() => releaseSeat(b.steam_id)}>
                        {t("account.seats.release")}
                      </Button>
                    )}
                  </Group>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}
    </Modal>
  );
}
